'use client';
import { useEffect, useState } from 'react';

// components
import { OrFilteredGrid } from '@/components/organisms';
import { HousePlansControls } from '@/components/organisms/or-filtered-grid/grid-controls/house-plans/HousePlansControls';

// configs
import { plansSortOptions } from '@/components/organisms/or-filtered-grid/sort-options/planSortConfig';

// types
import { IPromo } from '@/lib/contentstack-sdk/types';
import { IHousePlanHit } from '@/lib/search/types';
import {
  IFilterConfig,
  IFilterControlProps,
  IHomePlanFilteredGridProps,
  ISortOption,
} from '../or-filtered-grid.types';

// hooks
import { usePromos } from '@/hooks/usePromos';
import { useLocation } from '@/context/locationContext';
import {
  fetchExteriorStylesFacets,
  getPlansFilterConfig,
} from '../config/plansFilterConfig';
import { renderCard } from '../utils/renderCards';

export const HomePlanFilteredGrid = ({
  tooltipText,
}: IHomePlanFilteredGridProps) => {
  const [allPlans, setAllPlans] = useState<IHousePlanHit[]>([]);
  const [promo, setPromo] = useState<IPromo[] | []>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);

  // sorting + filtering vars
  const [availableSortOptions, setAvailableSortOptions] =
    useState<ISortOption[]>(plansSortOptions);
  const [availableControls, setAvailableControls] = useState<
    IFilterConfig<IHousePlanHit>[]
  >([]);
  const [visibleCount, setVisibleCount] = useState<number>(17);

  const { currentBuildStudio } = useLocation();
  const { getPromosByLocation } = usePromos();

  useEffect(() => {
    const fetchFilterConfig = async () => {
      const exteriorFacets = await fetchExteriorStylesFacets();
      const facets = getPlansFilterConfig(exteriorFacets);
      setAvailableControls(facets);
    };
    fetchFilterConfig();
  }, []);

  // add/remove price/hasModelTour from sorting and filtering based on location data
  useEffect(() => {
    const newSortOptions = currentBuildStudio?.Id
      ? plansSortOptions
      : plansSortOptions.filter((option) => !option.value.includes('price'));

    const newControls = currentBuildStudio?.Id
      ? availableControls
      : availableControls.filter(
          (filter) => filter.key !== 'Price' && filter.key !== 'HasModelTour',
        );
    setAvailableSortOptions(newSortOptions);
    setAvailableControls(newControls);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBuildStudio]);

  // fetch plans
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const params = new URLSearchParams();

        if (currentBuildStudio?.Id) {
          params.append('design_studio_id', String(currentBuildStudio.Id));
        }

        params.append('is_for_grid', 'true');

        const res = await fetch(`/algolia/home-plans?${params.toString()}`);

        if (!res.ok) {
          throw new Error(`Failed to fetch plans: ${res.statusText}`);
        }

        const plansData = await res.json();

        /* NO!!! By doing this you are only running what is shown in the GRID through the filter functions
            which will break when you try to filter exterior styles or other data that is elevation specific
        const prefPlans = plansData.filter(
          (homes: IHousePlanHit) => homes?.FloorPlans?.[0].IsPreferred,
        );
        setAllPlans(prefPlans);
        */

        setAllPlans(plansData);
      } catch (error) {
        console.error('Error fetching plans:', error);
        setAllPlans([]);
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchPlans();
  }, [currentBuildStudio?.Id]);

  // fetch promo
  useEffect(() => {
    const fetchPromo = () => {
      try {
        if (!currentBuildStudio?.Id) {
          setPromo([]);
          return;
        }

        const promoData = getPromosByLocation(currentBuildStudio?.Id);
        if (promoData.length > 0) {
          setPromo(promoData);
        }
      } catch (error) {
        console.error('Error fetching promo:', error);
        setPromo([]);
      }
    };

    fetchPromo();
  }, [currentBuildStudio, getPromosByLocation]);

  // reset the plans and visible count when the location selection updates
  useEffect(() => {
    setVisibleCount(17);
  }, [currentBuildStudio]);

  // the promo should not be filtered or sorted when added to the orFilteredGrid
  // it's passed as nonFilteredContent and requires an index to appear in the grid of data
  const getPromoIndex = (planCount: number) => {
    if (planCount < 3) return 0;
    if (planCount >= 3 && planCount < 6) return 5;
    return 9;
  };

  const totalFromStudio = allPlans.filter(
    (homes) => homes?.FloorPlans?.[0].IsPreferred,
  );

  const promoIndex = getPromoIndex(allPlans.length);

  const renderControls = ({
    config,
    filterValues,
    setFilter,
    resetFilters,
    filteredResults,
    sort,
    setSort,
  }: IFilterControlProps) => {
    return (
      <HousePlansControls
        tooltipText={tooltipText}
        config={config}
        filterValues={filterValues}
        setFilter={setFilter}
        resetFilters={resetFilters}
        filteredResults={filteredResults}
        totalResults={totalFromStudio.length}
        sort={sort}
        setSort={setSort}
        sortOptions={availableSortOptions}
        scrollListData={allPlans}
      />
    );
  };

  return (
    <OrFilteredGrid
      data={allPlans}
      config={availableControls}
      renderControls={renderControls}
      renderCard={renderCard}
      sortOptions={availableSortOptions}
      nonFilteredData={promo}
      nonFilteredDataIndex={promoIndex}
      visibleCards={visibleCount}
      shouldLazyLoad={true}
      initialSort="popular"
      isLoading={isDataLoading}
      restoreKey="home_plan_detail"
    />
  );
};
